/**
 * The dive (PLAN 4.2 item 2): from the globe to the twin of the selected
 * antenna and back.
 *
 * dive(antennaId, deps):
 *  1. resolve the antenna in the loaded dataset and its site;
 *  2. resolve what the dish points at (./pointing.ts) at the shared clock;
 *  3. resolve the asset: the antenna's twin mapping, else the parametric spec
 *     registered as `antenna_<antennaId>` (./asset.ts);
 *  4. prepare the globe: stop tracking, 3D scene mode, Google 3D tiles when the
 *     engine has ion assets (the previous values are put back afterwards);
 *  5. fly: a high approach, then a settle about 600 m above the dish (1,500 ms
 *     in all; one jump under reduced motion);
 *  6. fade the curtain to black with the site caption (300 ms, 0 under
 *     reduced motion), select the asset and open /maintenance;
 *  7. wait for the twin to load and its scene to publish the TwinHandle;
 *  8. set the dish pointing and the sun (solar position at clockNow());
 *  9. frame the dish at the globe view's bearing (a high arrival pose);
 * 10. fade in, descend to the hero pose, phase 'twin'. While in the twin the
 *     pointing refreshes every 5 s and the sun every minute.
 *
 * returnToGlobe(): fade out, open the route the dive started from with the
 * antenna selected again, jump the new globe close over the site, fade in and
 * fly out to 5,000 km.
 *
 * Every wait has a timeout and a failure names its step: the dive toasts
 * "Dive failed while <step>: <reason>", puts back the globe settings and the
 * route it changed, and ends in 'idle' with the reason in useDive.error. All
 * side effects go through DiveDeps (./deps.ts builds the real ones), so the
 * state machine runs under test with fakes.
 */
import type { ToastLevel } from '@/components/ui/toast';
import type { SceneMode, FlyToOrientation } from '@/features/loader/globe/GlobeEngine';
import type { ActiveContact } from '@/features/loader/store';
import type { Antenna, ContactWindowMap, NetworkDataset } from '@/features/loader/types';
import { compassToAssetAzimuth, DEFAULT_HEADING_DEG } from '@/features/twin/scene/pointing';
import type { TwinLoadStatus } from '@/features/twin/store';
import type { AssetSpec, CameraPose } from '@/features/twin/types';
import type { Selection } from '@/state/store';
import {
  buildDiveAssetSpec,
  diveAssetId,
  diveCameraPose,
  dishTopHeightM,
  GLOBE_SETTLE_HEADING_DEG,
  twinAssetIdOf,
  TWIN_ARRIVAL_ELEVATION_DEG,
  TWIN_HERO_ELEVATION_DEG,
} from './asset';
import { resolveDivePointing, type DivePointing, type PointingContext } from './pointing';
import { solarPosition } from './solar';
import { useDive, type DiveSite, type DiveTrack } from './store';

/** Route of the twin stage the dive opens. */
export const MAINTENANCE_PATH = '/maintenance';

export const DIVE_TIMING = {
  /** Curtain fade (PLAN 4.3: 150 to 300 ms transitions). */
  fadeMs: 300,
  /** Approach stage: to this height above the site. */
  approachHeightM: 30_000,
  approachMs: 900,
  /** Settle stage: this far above the top of the dish. Approach plus settle is the 1,500 ms flight. */
  settleAboveDishM: 600,
  settleMs: 600,
  /** Slack after a timed flight so its last frame lands before the next step. */
  flightMarginMs: 120,
  /** Twin camera descent from the arrival pose to the hero pose. */
  arrivalDescentMs: 1_200,
  /** Return flight: out to this height above the site. */
  returnHeightM: 5_000_000,
  returnFlightMs: 1_500,
  pointingRefreshMs: 5_000,
  sunRefreshMs: 60_000,
  pollMs: 50,
} as const;

export const DIVE_TIMEOUTS = {
  sceneMode: 5_000,
  twinLoad: 30_000,
  /** The twin stage is a lazy chunk; a cold dev server and software WebGL are slow to start it. */
  twinScene: 45_000,
  camera: 15_000,
  descent: 10_000,
  globeMount: 45_000,
} as const;

export type DiveStep =
  | 'start'
  | 'antenna'
  | 'pointing'
  | 'asset'
  | 'globe'
  | 'scene-mode'
  | 'flight'
  | 'open-twin'
  | 'twin-load'
  | 'twin-scene'
  | 'camera'
  | 'return-globe'
  | 'return-flight';

export const DIVE_STEP_LABEL: Record<DiveStep, string> = {
  start: 'starting the dive',
  antenna: 'resolving the antenna',
  pointing: 'pointing the dish',
  asset: 'resolving the twin asset',
  globe: 'preparing the globe',
  'scene-mode': 'switching the globe to 3D',
  flight: 'flying to the site',
  'open-twin': 'opening the twin',
  'twin-load': 'loading the twin',
  'twin-scene': 'starting the twin scene',
  camera: 'framing the dish',
  'return-globe': 'reopening the globe',
  'return-flight': 'flying back out',
};

export class DiveStepError extends Error {
  readonly step: DiveStep;

  constructor(step: DiveStep, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'DiveStepError';
    this.step = step;
  }
}

/** Thrown inside a superseded flow (the dive was left or reset meanwhile); never surfaced. */
class DiveCancelled extends Error {
  constructor() {
    super('dive superseded');
    this.name = 'DiveCancelled';
  }
}

export type DiveOutcome = { ok: true } | { ok: false; step: DiveStep; message: string };

// ------------------------------------------------------------------ deps

/** The part of GlobeEngine the dive drives. */
export interface DiveGlobe {
  flyTo(lon: number, lat: number, heightM?: number, durationS?: number, orientation?: FlyToOrientation): void;
  getGroundHeight?(lon: number, lat: number): number | null;
  getSceneMode(): SceneMode;
}

export interface DiveLoaderView {
  inspectedPass?: { missionId: string; antennaId: string; startMs: number; endMs: number } | null;
  dataset: NetworkDataset | null;
  activeContacts: ActiveContact[];
  contactWindows: ContactWindowMap | null;
  sceneMode: SceneMode;
  google3D: boolean;
}

export interface DiveTwinView {
  assetId: string | null;
  loadStatus: TwinLoadStatus;
  loadError: string | null;
  subviewId: string | null;
  /** site.headingDeg of the loaded asset, undefined when it has none. */
  headingDeg: number | undefined;
}

/** The part of TwinHandle the dive drives. */
export interface DiveTwinHandle {
  getCameraPose(): CameraPose;
  flyTo(target: { pose?: CameraPose; poseId?: string }, durationMs?: number): Promise<void>;
  setSun(azimuthDeg: number, elevationDeg: number): void;
}

export interface DiveDeps {
  loader(): DiveLoaderView;
  setGlobeView(patch: { sceneMode?: SceneMode; google3D?: boolean }): void;
  stopTracking(): void;
  /** The mounted globe engine, null while no globe mode is on screen. */
  globe(): DiveGlobe | null;
  /** True when the engine can load ion assets (Google 3D tiles). */
  ionAssets(): boolean;
  /** Current route with its query, where the return goes back to. */
  currentPath(): string;
  navigate(path: string): void;
  select(partial: Partial<Selection>): void;
  twin(): DiveTwinView;
  closeSubview(): void;
  twinHandle(): DiveTwinHandle | null;
  setPointing(pointing: { azimuthDeg: number; elevationDeg: number } | null): void;
  registerAssetSpec(assetId: string, spec: AssetSpec): void;
  /** The shared clock (clockNow): pointing and sun follow it. */
  now(): number;
  /** Monotonic wall time for timeouts. */
  monotonicMs(): number;
  sleep(ms: number): Promise<void>;
  nextFrame(): Promise<void>;
  /** Calls fn every ms until the returned function is called. */
  every(ms: number, fn: () => void): () => void;
  reducedMotion(): boolean;
  toast(level: ToastLevel, text: string): void;
}

// --------------------------------------------------------------- session

interface Session {
  deps: DiveDeps;
  antenna: Antenna;
  site: DiveSite;
  assetId: string;
  fromPath: string;
  reduced: boolean;
  fadeMs: number;
  /** Globe view settings the dive changed, with the values to put back. */
  restore: { sceneMode?: SceneMode; google3D?: boolean };
  navigatedToTwin: boolean;
  pointingSet: boolean;
  /** Heading of the loaded asset (compass bearing of its +Z axis). */
  headingDeg: number;
  stopTimers: (() => void)[];
}

let session: Session | null = null;

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function seconds(ms: number): string {
  return `${Math.round(ms / 100) / 10} s`;
}

function alive(s: Session): void {
  if (session !== s) throw new DiveCancelled();
}

async function pause(s: Session, ms: number): Promise<void> {
  if (ms > 0) await s.deps.sleep(ms);
  alive(s);
}

/** Polls `probe` until it returns a truthy value; a throw inside it fails the step with its message. */
async function waitUntil<T>(
  s: Session,
  step: DiveStep,
  timeoutMs: number,
  probe: () => T | null | undefined | false,
  what: string,
): Promise<T> {
  const start = s.deps.monotonicMs();
  for (;;) {
    alive(s);
    let value: T | null | undefined | false;
    try {
      value = probe();
    } catch (err) {
      throw new DiveStepError(step, errorText(err), { cause: err });
    }
    if (value) return value;
    if (s.deps.monotonicMs() - start >= timeoutMs)
      throw new DiveStepError(step, `timed out after ${seconds(timeoutMs)} waiting for ${what}`);
    await s.deps.sleep(DIVE_TIMING.pollMs);
  }
}

/** Waits for a promise with a timeout; a rejection fails the step with its message. */
async function settle<T>(
  s: Session,
  step: DiveStep,
  timeoutMs: number,
  promise: Promise<T>,
  what: string,
): Promise<T> {
  const box: { done: boolean; failed: boolean; value?: T; error?: unknown } = { done: false, failed: false };
  promise.then(
    (value) => {
      box.done = true;
      box.value = value;
    },
    (error: unknown) => {
      box.failed = true;
      box.error = error;
    },
  );
  const start = s.deps.monotonicMs();
  for (;;) {
    // Let an already settled promise report before the first check.
    await Promise.resolve();
    alive(s);
    if (box.failed) throw new DiveStepError(step, errorText(box.error), { cause: box.error });
    if (box.done) return box.value as T;
    if (s.deps.monotonicMs() - start >= timeoutMs)
      throw new DiveStepError(step, `timed out after ${seconds(timeoutMs)} waiting for ${what}`);
    await s.deps.sleep(DIVE_TIMING.pollMs);
  }
}

/** Runs one step of a flow: any failure inside it is reported as that step. */
async function inStep<T>(step: DiveStep, run: () => T | Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    if (err instanceof DiveStepError || err instanceof DiveCancelled) throw err;
    throw new DiveStepError(step, errorText(err), { cause: err });
  }
}

function requireGlobe(s: Session, step: DiveStep): DiveGlobe {
  const globe = s.deps.globe();
  if (!globe) throw new DiveStepError(step, 'the globe is no longer on screen');
  return globe;
}

function siteOf(antenna: Antenna): DiveSite {
  return {
    antennaId: antenna.id,
    name: antenna.name,
    shortName: antenna.shortName,
    place: [antenna.location.name, antenna.location.country].filter(Boolean).join(', '),
    lat: antenna.location.lat,
    lon: antenna.location.lon,
  };
}

function pointingContext(view: DiveLoaderView): PointingContext {
  return {
    inspectedPass: view.inspectedPass,
    missions: view.dataset?.missions ?? [],
    activeContacts: view.activeContacts,
    contactWindows: view.contactWindows,
  };
}

function utcClock(ms: number): string {
  return `${new Date(ms).toISOString().slice(11, 16)} UTC`;
}

/** Caption and HUD line for a pointing. */
export function trackOf(pointing: DivePointing, dataset: NetworkDataset | null): DiveTrack {
  const name = pointing.missionId
    ? (dataset?.missions.find((m) => m.id === pointing.missionId)?.shortName ?? pointing.missionId)
    : null;
  const label =
    pointing.source === 'contact'
      ? `tracking ${name}`
      : pointing.source === 'scheduled' || pointing.source === 'pass-preview'
        ? `${name} predicted pass in progress`
        : pointing.source === 'next-window'
          ? `waiting for ${name}, AOS ${pointing.windowStartMs !== null ? utcClock(pointing.windowStartMs) : 'pending'}`
          : 'stowed, no pass scheduled';
  return {
    source: pointing.source,
    missionId: pointing.missionId,
    label,
    azimuthDeg: pointing.azimuthDeg,
    elevationDeg: pointing.elevationDeg,
  };
}

function applySun(s: Session, handle: DiveTwinHandle): void {
  const sun = solarPosition(s.site.lat, s.site.lon, s.deps.now());
  handle.setSun(compassToAssetAzimuth(sun.azimuthDeg, s.headingDeg), sun.elevationDeg);
}

function stopTimers(s: Session): void {
  for (const stop of s.stopTimers) stop();
  s.stopTimers = [];
}

/** Stops the refreshes, clears the pointing and puts back the globe settings the dive changed. */
function teardown(s: Session): void {
  stopTimers(s);
  if (s.pointingSet) {
    s.deps.setPointing(null);
    s.pointingSet = false;
  }
  const patch = { ...s.restore };
  s.restore = {};
  if (patch.sceneMode !== undefined || patch.google3D !== undefined) s.deps.setGlobeView(patch);
}

// ------------------------------------------------------------------ dive

function settleOverSite(s: Session, globe: DiveGlobe, duration: number): void {
  const { site } = s;
  const heightAboveGround = DIVE_TIMING.settleAboveDishM + dishTopHeightM(s.antenna);
  const ground = globe.getGroundHeight?.(site.lon, site.lat) ?? 0;
  const offsetM = heightAboveGround / Math.tan((TWIN_ARRIVAL_ELEVATION_DEG * Math.PI) / 180);
  const cameraLat = Math.max(-89.99, Math.min(89.99, site.lat - offsetM / 111_320));
  globe.flyTo(site.lon, cameraLat, ground + heightAboveGround, duration, {
    headingDeg: GLOBE_SETTLE_HEADING_DEG,
    pitchDeg: -TWIN_ARRIVAL_ELEVATION_DEG,
  });
}

async function flyToSite(s: Session): Promise<void> {
  const { site, deps } = s;
  if (s.reduced) {
    settleOverSite(s, requireGlobe(s, 'flight'), 0);
    await deps.nextFrame();
    await deps.nextFrame();
    alive(s);
    requireGlobe(s, 'flight');
    return;
  }
  requireGlobe(s, 'flight').flyTo(
    site.lon,
    site.lat,
    DIVE_TIMING.approachHeightM,
    DIVE_TIMING.approachMs / 1000,
  );
  await pause(s, DIVE_TIMING.approachMs);
  settleOverSite(s, requireGlobe(s, 'flight'), DIVE_TIMING.settleMs / 1000);
  await pause(s, DIVE_TIMING.settleMs + DIVE_TIMING.flightMarginMs);
  requireGlobe(s, 'flight');
}

/** Frames the dish at the globe view's bearing: the arrival pose now, the hero pose returned for the descent. */
async function frameDish(s: Session, handle: DiveTwinHandle): Promise<CameraPose> {
  // The main pose refits to the loaded model; its target and distance frame the site.
  await settle(
    s,
    'camera',
    DIVE_TIMEOUTS.camera,
    handle.flyTo({ poseId: 'main' }, 0),
    'the twin to frame its model',
  );
  const main = handle.getCameraPose();
  const distance = Math.hypot(
    main.position[0] - main.target[0],
    main.position[1] - main.target[1],
    main.position[2] - main.target[2],
  );
  if (!(distance > 0) || !Number.isFinite(distance))
    throw new DiveStepError('camera', `the twin camera has no usable distance to its target (${distance})`);
  const pose = (elevationDeg: number) =>
    diveCameraPose(main.target, distance, elevationDeg, s.headingDeg, GLOBE_SETTLE_HEADING_DEG, main.fov);
  const hero = pose(TWIN_HERO_ELEVATION_DEG);
  await settle(
    s,
    'camera',
    DIVE_TIMEOUTS.camera,
    handle.flyTo({ pose: s.reduced ? hero : pose(TWIN_ARRIVAL_ELEVATION_DEG) }, 0),
    'the arrival framing',
  );
  return hero;
}

function startRefreshes(s: Session): void {
  const { deps } = s;
  const refreshPointing = () => {
    if (session !== s || useDive.getState().phase !== 'twin') return;
    try {
      const view = deps.loader();
      const pointing = resolveDivePointing(s.antenna, pointingContext(view), deps.now());
      deps.setPointing({ azimuthDeg: pointing.azimuthDeg, elevationDeg: pointing.elevationDeg });
      s.pointingSet = true;
      useDive.getState().setTrack(trackOf(pointing, view.dataset));
    } catch (err) {
      console.error('[dive] pointing refresh failed; the dish stops following the pass:', err);
      deps.toast('error', `The dish stopped following the pass: ${errorText(err)}`);
      stopTimers(s);
    }
  };
  const refreshSun = () => {
    if (session !== s || useDive.getState().phase !== 'twin') return;
    const handle = deps.twinHandle();
    if (!handle) return;
    try {
      applySun(s, handle);
    } catch (err) {
      console.error('[dive] sun refresh failed:', err);
      deps.toast('error', `The sun position could not be updated: ${errorText(err)}`);
      stopTimers(s);
    }
  };
  s.stopTimers.push(deps.every(DIVE_TIMING.pointingRefreshMs, refreshPointing));
  s.stopTimers.push(deps.every(DIVE_TIMING.sunRefreshMs, refreshSun));
}

export async function dive(antennaId: string, deps: DiveDeps): Promise<DiveOutcome> {
  if (session || useDive.getState().phase !== 'idle') {
    deps.toast('warn', 'A dive is already in progress.');
    return { ok: false, step: 'start', message: 'a dive is already in progress' };
  }
  const reduced = deps.reducedMotion();
  const fadeMs = reduced ? 0 : DIVE_TIMING.fadeMs;
  useDive.getState().begin(antennaId, fadeMs);
  let current: Session | null = null;
  try {
    // 1. Antenna and site.
    const view = deps.loader();
    const antenna = view.dataset?.antennas.find((a) => a.id === antennaId);
    if (!antenna) {
      throw new DiveStepError(
        'antenna',
        `antenna ${antennaId} is not in the loaded ${view.dataset ? `${view.dataset.network} ` : ''}dataset`,
      );
    }
    const { lat, lon } = antenna.location ?? {};
    if (
      typeof lat !== 'number' ||
      typeof lon !== 'number' ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lon) ||
      Math.abs(lat) > 90 ||
      Math.abs(lon) > 180
    ) {
      throw new DiveStepError(
        'antenna',
        `${antenna.shortName} has no usable location (lat ${lat}, lon ${lon})`,
      );
    }

    // 2. What the dish points at, now.
    const pointing = await inStep('pointing', () =>
      resolveDivePointing(antenna, pointingContext(view), deps.now()),
    );

    // 3. The asset: the antenna's twin mapping, else its parametric spec facing the pointing azimuth.
    const assetId = await inStep('asset', () => {
      const mapped = twinAssetIdOf(antenna);
      if (mapped) return mapped;
      const id = diveAssetId(antenna.id);
      try {
        deps.registerAssetSpec(id, buildDiveAssetSpec(antenna, pointing.azimuthDeg));
      } catch (err) {
        throw new Error(`the parametric twin of ${antenna.shortName} is invalid: ${errorText(err)}`, {
          cause: err,
        });
      }
      return id;
    });

    const s: Session = {
      deps,
      antenna,
      site: siteOf(antenna),
      assetId,
      fromPath: deps.currentPath(),
      reduced,
      fadeMs,
      restore: {},
      navigatedToTwin: false,
      pointingSet: false,
      headingDeg: DEFAULT_HEADING_DEG,
      stopTimers: [],
    };
    current = s;
    session = s;
    useDive.getState().setTarget(assetId, s.site);
    useDive.getState().setTrack(trackOf(pointing, view.dataset));

    // 4. Globe: no tracking, 3D (the photoreal tiles need it), Google 3D when the engine has ion assets.
    await inStep('globe', async () => {
      if (!deps.globe()) throw new Error('the globe is not on screen');
      deps.stopTracking();
      if (view.sceneMode !== '3D') {
        s.restore.sceneMode = view.sceneMode;
        deps.setGlobeView({ sceneMode: '3D' });
        await waitUntil(
          s,
          'scene-mode',
          DIVE_TIMEOUTS.sceneMode,
          () => deps.globe()?.getSceneMode() === '3D',
          'the globe to switch to 3D',
        );
      }
      if (deps.ionAssets() && !view.google3D) {
        s.restore.google3D = view.google3D;
        deps.setGlobeView({ google3D: true });
      }
    });

    // 5. Flight to the site.
    await inStep('flight', () => flyToSite(s));

    // 6. Curtain, then the twin route with the asset selected.
    useDive.getState().setPhase('fading');
    useDive.getState().setCurtain(true);
    await pause(s, fadeMs);
    await inStep('open-twin', () => {
      const before = deps.twin();
      if (before.assetId === assetId && before.subviewId) deps.closeSubview();
      deps.select({ assetId, subsystemId: null });
      s.navigatedToTwin = true;
      deps.navigate(MAINTENANCE_PATH);
    });

    // 7. Twin loaded, scene mounted.
    await waitUntil(
      s,
      'twin-load',
      DIVE_TIMEOUTS.twinLoad,
      () => {
        const t = deps.twin();
        if (t.assetId !== assetId) return false;
        if (t.loadStatus === 'error') throw new Error(t.loadError ?? `the twin ${assetId} failed to load`);
        return t.loadStatus === 'ready';
      },
      `the twin ${assetId} to load`,
    );
    const handle = await waitUntil(
      s,
      'twin-scene',
      DIVE_TIMEOUTS.twinScene,
      () => deps.twinHandle(),
      'the twin scene to start',
    );

    // 8. Pointing and sun.
    await inStep('pointing', () => {
      s.headingDeg = deps.twin().headingDeg ?? DEFAULT_HEADING_DEG;
      s.pointingSet = true;
      deps.setPointing({ azimuthDeg: pointing.azimuthDeg, elevationDeg: pointing.elevationDeg });
      applySun(s, handle);
    });

    // 9. Camera at the globe view's bearing.
    const hero = await inStep('camera', () => frameDish(s, handle));

    // 10. Fade in and descend.
    useDive.getState().setCurtain(false);
    await pause(s, fadeMs);
    if (!reduced)
      await settle(
        s,
        'camera',
        DIVE_TIMEOUTS.descent,
        handle.flyTo({ pose: hero }, DIVE_TIMING.arrivalDescentMs),
        'the arrival descent',
      );
    useDive.getState().setPhase('twin');
    startRefreshes(s);
    return { ok: true };
  } catch (err) {
    return fail(deps, current, err, 'dive');
  }
}

export async function returnToGlobe(depsOverride?: DiveDeps): Promise<DiveOutcome> {
  const s = session;
  if (!s || useDive.getState().phase !== 'twin') {
    (depsOverride ?? s?.deps)?.toast('warn', 'There is no dive to return from.');
    return { ok: false, step: 'return-globe', message: 'no dive is in the twin' };
  }
  if (depsOverride) s.deps = depsOverride;
  const { deps } = s;
  useDive.getState().setPhase('returning');
  stopTimers(s);
  try {
    useDive.getState().setCurtain(true);
    await pause(s, s.fadeMs);
    // Pointing cleared; the globe settings go back before the globe remounts, so it opens in
    // the user's scene mode and does not start a Google 3D load for the flight out.
    teardown(s);
    deps.navigate(s.fromPath);
    deps.select({ antennaId: s.antenna.id, missionId: null, subsystemId: null });
    s.navigatedToTwin = false;
    await waitUntil(s, 'return-globe', DIVE_TIMEOUTS.globeMount, () => deps.globe(), 'the globe to mount');
    // Let the new engine finish its first framing (a 2D map fits itself on the next frame).
    for (let i = 0; i < 3; i++) await deps.nextFrame();
    alive(s);
    const { lon, lat } = s.site;
    if (s.reduced) {
      requireGlobe(s, 'return-flight').flyTo(lon, lat, DIVE_TIMING.returnHeightM, 0);
      await deps.nextFrame();
      alive(s);
      useDive.getState().setCurtain(false);
    } else {
      // The dive in reverse: open over the dish, then rise to the globe.
      settleOverSite(s, requireGlobe(s, 'return-flight'), 0);
      await deps.nextFrame();
      alive(s);
      useDive.getState().setCurtain(false);
      await pause(s, s.fadeMs);
      requireGlobe(s, 'return-flight').flyTo(
        lon,
        lat,
        DIVE_TIMING.returnHeightM,
        DIVE_TIMING.returnFlightMs / 1000,
      );
      await pause(s, DIVE_TIMING.returnFlightMs + DIVE_TIMING.flightMarginMs);
      requireGlobe(s, 'return-flight');
    }
    session = null;
    useDive.getState().end();
    return { ok: true };
  } catch (err) {
    return fail(deps, s, err, 'return');
  }
}

/** Ends a dive that settled in the twin without the return flight (the user left the twin or its asset). */
export function abandonDive(reason: string): void {
  const s = session;
  if (!s || useDive.getState().phase !== 'twin') return;
  console.info(`[dive] ended without the return flight: ${reason}`);
  teardown(s);
  session = null;
  useDive.getState().end();
}

/** Drops any session without side effects (tests, hot reload). */
export function resetDive(): void {
  if (session) stopTimers(session);
  session = null;
  useDive.getState().end();
}

async function fail(
  deps: DiveDeps,
  s: Session | null,
  err: unknown,
  flow: 'dive' | 'return',
): Promise<DiveOutcome> {
  if (err instanceof DiveCancelled) return { ok: false, step: 'start', message: 'superseded' };
  const step: DiveStep = err instanceof DiveStepError ? err.step : flow === 'dive' ? 'start' : 'return-globe';
  const message = errorText(err);
  const label = DIVE_STEP_LABEL[step];
  console.error(`[dive] ${flow === 'dive' ? 'dive' : 'return'} failed while ${label}:`, err);
  deps.toast(
    'error',
    `${flow === 'dive' ? 'Dive' : 'Return to the globe'} failed while ${label}: ${message}`,
  );
  if (s) {
    teardown(s);
    if (s.navigatedToTwin) {
      s.navigatedToTwin = false;
      deps.navigate(s.fromPath);
      deps.select({ antennaId: s.antenna.id, missionId: null });
    }
    if (session === s) session = null;
  }
  // Lift the curtain, keep the caption through its fade, then go idle.
  useDive.getState().setCurtain(false);
  const fadeMs = useDive.getState().fadeMs;
  if (fadeMs > 0) await deps.sleep(fadeMs);
  useDive.getState().end(`${label}: ${message}`);
  return { ok: false, step, message };
}
